import { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FileText, Plus, Trash2, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import ContractEditablePreview from "./ContractEditablePreview";

const EMPTY_PARTY = {
  nome_cognome: "",
  nato_a: "",
  nato_il: "",
  residenza: "",
  via_residenza: "",
  civico_residenza: "",
  documento: "",
  codice_fiscale: "",
};

function PartyEditor({ role, index, party, onChange, onRemove, canRemove }) {
  const set = (k, v) => onChange({ ...party, [k]: v });
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60 space-y-2" data-testid={`cg-${role}-${index}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          {role === "locatore" ? "Locatore" : "Conduttore"} #{index + 1}
        </p>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-red-700 hover:text-red-900 flex items-center gap-1 text-xs"
            data-testid={`cg-${role}-${index}-remove`}
          >
            <Trash2 size={13} /> Rimuovi
          </button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Nome e cognome / Ragione sociale *</Label>
          <Input className="mt-1 h-9" value={party.nome_cognome} onChange={(e) => set("nome_cognome", e.target.value)} data-testid={`cg-${role}-${index}-nome`} />
        </div>
        <div>
          <Label className="text-xs">Codice fiscale / P.IVA</Label>
          <Input className="mt-1 h-9" value={party.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} data-testid={`cg-${role}-${index}-cf`} />
        </div>
        <div>
          <Label className="text-xs">Luogo di nascita</Label>
          <Input className="mt-1 h-9" value={party.nato_a} onChange={(e) => set("nato_a", e.target.value)} data-testid={`cg-${role}-${index}-nato-a`} />
        </div>
        <div>
          <Label className="text-xs">Data di nascita</Label>
          <Input type="date" className="mt-1 h-9" value={party.nato_il} onChange={(e) => set("nato_il", e.target.value)} data-testid={`cg-${role}-${index}-nato-il`} />
        </div>
        <div>
          <Label className="text-xs">Comune di residenza</Label>
          <Input className="mt-1 h-9" value={party.residenza} onChange={(e) => set("residenza", e.target.value)} data-testid={`cg-${role}-${index}-residenza`} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Label className="text-xs">Via</Label>
            <Input className="mt-1 h-9" value={party.via_residenza} onChange={(e) => set("via_residenza", e.target.value)} data-testid={`cg-${role}-${index}-via`} />
          </div>
          <div>
            <Label className="text-xs">Civico</Label>
            <Input className="mt-1 h-9" value={party.civico_residenza} onChange={(e) => set("civico_residenza", e.target.value)} data-testid={`cg-${role}-${index}-civ`} />
          </div>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Documento di riconoscimento (tipo ed estremi)</Label>
          <Input className="mt-1 h-9" placeholder="Es. C.I. AY1234567 oppure Passaporto YA12345…" value={party.documento} onChange={(e) => set("documento", e.target.value)} data-testid={`cg-${role}-${index}-doc`} />
        </div>
      </div>
    </div>
  );
}

export default function ContractGeneratorModal({
  open, onOpenChange, submissionId, data, onGenerated,
}) {
  const [step, setStep] = useState(1); // 1 = form, 2 = success
  const [submitting, setSubmitting] = useState(false);

  // Prefill from FormContext — supports multi-locatore/conduttore (Step 2/3 arrays)
  const initialLocatoriArr =
    Array.isArray(data?.locatori) && data.locatori.length
      ? data.locatori.map((p) => ({ ...EMPTY_PARTY, nome_cognome: p?.nome || "" }))
      : [{ ...EMPTY_PARTY, nome_cognome: data?.locatore?.nome || "" }];
  const initialConduttoriArr =
    Array.isArray(data?.conduttori) && data.conduttori.length
      ? data.conduttori.map((p) => ({ ...EMPTY_PARTY, nome_cognome: p?.nome || "" }))
      : [{ ...EMPTY_PARTY, nome_cognome: data?.conduttore?.nome || "" }];

  const [contractType, setContractType] = useState(data?.contratto?.tipologia === "transitorio" ? "transitorio" : data?.contratto?.tipologia === "studenti" ? "studenti" : "abitativo");
  const [locatori, setLocatori] = useState(initialLocatoriArr);
  const [conduttori, setConduttori] = useState(initialConduttoriArr);
  // Auto-detect scope oggetto locazione dall'attestazione bilaterale (Step 8):
  //   - checkbox "Affitto solo una porzione dell'appartamento" attiva, OPPURE
  //   - locazione parziale storica (in_stanze≥2 o spazi_comuni>0)
  // In tutti gli altri casi → intera unità immobiliare.
  // Questa scelta rispecchia esattamente l'attestazione bilaterale: il
  // locatore NON può modificarla da questo modal — per cambiare deve
  // tornare allo Step 8 "Tipologia Superficie e MQ".
  const oggettoScope = useMemo(() => {
    const isPorzioneFlag = !!data?.superfici?.locazione_porzione;
    const stanze = Number(data?.superfici?.in_stanze) || 0;
    const spazi = Number(data?.superfici?.spazi_comuni) || 0;
    return isPorzioneFlag || stanze >= 2 || spazi > 0 ? "porzione" : "totale";
  }, [data?.superfici?.locazione_porzione, data?.superfici?.in_stanze, data?.superfici?.spazi_comuni]);
  const [porzioneUsoRegolato, setPorzioneUsoRegolato] = useState("");
  const [immobile, setImmobile] = useState({
    piano: data?.immobile?.piano || "",
    scala: "",
    interno: "",
    vani: "",
  });
  // Keep the (locked) piano in sync with the immobile data even if it changes
  // while the modal is mounted. Piano must ALWAYS reflect what was declared
  // in the earlier steps — no manual override.
  useEffect(() => {
    const src = data?.immobile?.piano || "";
    if (src !== immobile.piano) {
      setImmobile((im) => ({ ...im, piano: src }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.immobile?.piano]);
  // Punto 4 — "Ammobiliato" è una scelta UFFICIALE fatta nello Step 9
  //   (data.step9.ammobiliato = "si" | "no" | ""). Il contratto rispecchia
  //   quella scelta e NON è modificabile da qui: per cambiarla, il locatore
  //   deve tornare allo Step 9 "Maggiorazioni/Riduzioni".
  const ammobiliata = useMemo(() => {
    const v = String(data?.step9?.ammobiliato || "").toLowerCase();
    if (v === "si" || v === "sì") return true;
    if (v === "no") return false;
    // Fallback legacy — tolleriamo valori boolean/altre forme da vecchie bozze
    const legacy = data?.immobile?.ammobiliato ?? data?.catasto?.arredamento;
    if (typeof legacy === "boolean") return legacy;
    const s = String(legacy || "").toLowerCase();
    if (s === "si" || s === "sì" || s === "true" || s === "yes") return true;
    return false;
  }, [data?.step9?.ammobiliato, data?.immobile?.ammobiliato, data?.catasto?.arredamento]);
  const [ape, setApe] = useState({ numero: "", data: "" });
  const [tabelle, setTabelle] = useState({ proprieta: "", riscaldamento: "", acqua: "", altre: "" });
  const [contratto, setContratto] = useState({
    durata_anni: 3,
    durata_mesi: 12,
    data_inizio: "",
    data_fine: "",
  });
  const [canone, setCanone] = useState({
    canone_annuo: "",
    numero_rate: 12,
    rata_mensile: "",
    scadenze_rate: "entro il giorno 5 di ogni mese",
    iban: "",
  });
  const [deposito, setDeposito] = useState({
    versato: true,
    importo: "",
    mensilita: 2,
  });
  const [altreGaranzie, setAltreGaranzie] = useState("");
  // Garanti (facoltativi) — lista di nomi liberi; vengono inseriti come
  // riga firma dedicata nel contratto e firmati dal portale pubblico
  // conduttore. Non è una raccolta dati strutturata (no CF/email).
  const [garanti, setGaranti] = useState([]);
  const [cedolareSecca, setCedolareSecca] = useState(false);
  const [esigenzaTransitorieta, setEsigenzaTransitorieta] = useState("");
  const [esigenzaLocatore, setEsigenzaLocatore] = useState("");
  const [esigenzaConduttore, setEsigenzaConduttore] = useState("");
  const [documentiEsigenza, setDocumentiEsigenza] = useState("");
  const [corso, setCorso] = useState("");
  const [ateneo, setAteneo] = useState("");
  // --- Fase 2: nuovi campi editabili ---
  // Punto 13 — oneri accessori mensili (importo €)
  const [quotaOneriMensile, setQuotaOneriMensile] = useState("");
  // Punto 14 — conviventi (textarea libera)
  const [conviventi, setConviventi] = useState("");
  // Punto 15 — recesso: mesi (1-6) + rimozione "gravi motivi"
  const [recessoMesi, setRecessoMesi] = useState(6);
  const [recessoGraviMotivi, setRecessoGraviMotivi] = useState(true);
  // Punto 16 — art. Consegna (stato immobile)
  const [consegnaStato, setConsegnaStato] = useState("");
  // Punto 17 — art. Accesso / modalità visite
  const [modalitaVisita, setModalitaVisita] = useState("");
  // Requirement 4 — Modalità di subentro (clausola editabile, usata nel contratto studenti)
  const [modalitaSubentro, setModalitaSubentro] = useState("");
  const [luogoFirma, setLuogoFirma] = useState("Milano");
  const [dataFirma, setDataFirma] = useState(new Date().toISOString().slice(0, 10));

  // =========================================================================
  // Persistenza bozza (localStorage, scoped per submissionId).
  //   • Auto-save con debounce 500ms a ogni modifica del form.
  //   • Caricamento automatico quando il modal viene aperto se esiste una
  //     bozza salvata per la stessa pratica.
  //   • Nessun autoclear dopo generazione PDF: la bozza resta finché il
  //     locatore non clicca "Scarta bozza" manualmente.
  //   • Mini-indicatore "Bozza salvata · Xs fa" mostrato in cima al modal.
  // =========================================================================
  const draftKey = useMemo(
    () => (submissionId ? `cc_cg_draft_${submissionId}` : null),
    [submissionId]
  );
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  // Gate che indica se la bozza è stata caricata. Usiamo useState (non useRef)
  // perché cambi di `draftLoaded` triggerano la save effect: in questo modo
  // il primo save avviene SOLO dopo che gli setter della load effect sono
  // stati processati (evitando l'overwrite della bozza con lo stato
  // iniziale di default).
  const [draftLoaded, setDraftLoaded] = useState(false);

  // 1) Load draft once when modal opens (if a persisted version exists)
  useEffect(() => {
    if (!open || !draftKey || draftLoaded) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && typeof d === "object") {
          if (d.contractType) setContractType(d.contractType);
          if (Array.isArray(d.locatori)) setLocatori(d.locatori);
          if (Array.isArray(d.conduttori)) setConduttori(d.conduttori);
          if (typeof d.porzioneUsoRegolato === "string") setPorzioneUsoRegolato(d.porzioneUsoRegolato);
          if (d.immobile) setImmobile((prev) => ({ ...prev, ...d.immobile, piano: prev.piano }));
          if (d.ape) setApe(d.ape);
          if (d.tabelle) setTabelle(d.tabelle);
          if (d.contratto) setContratto(d.contratto);
          if (d.canone) setCanone(d.canone);
          if (d.deposito) setDeposito(d.deposito);
          if (typeof d.altreGaranzie === "string") setAltreGaranzie(d.altreGaranzie);
          if (Array.isArray(d.garanti)) {
            setGaranti(
              d.garanti
                .map((g) => (typeof g === "string" ? g : (g?.nome || "")))
                .filter(Boolean)
            );
          }
          if (typeof d.cedolareSecca === "boolean") setCedolareSecca(d.cedolareSecca);
          if (typeof d.esigenzaTransitorieta === "string") setEsigenzaTransitorieta(d.esigenzaTransitorieta);
          if (typeof d.esigenzaLocatore === "string") setEsigenzaLocatore(d.esigenzaLocatore);
          if (typeof d.esigenzaConduttore === "string") setEsigenzaConduttore(d.esigenzaConduttore);
          if (typeof d.documentiEsigenza === "string") setDocumentiEsigenza(d.documentiEsigenza);
          if (typeof d.corso === "string") setCorso(d.corso);
          if (typeof d.ateneo === "string") setAteneo(d.ateneo);
          if (typeof d.quotaOneriMensile === "string") setQuotaOneriMensile(d.quotaOneriMensile);
          if (typeof d.conviventi === "string") setConviventi(d.conviventi);
          if (typeof d.recessoMesi === "number") setRecessoMesi(d.recessoMesi);
          if (typeof d.recessoGraviMotivi === "boolean") setRecessoGraviMotivi(d.recessoGraviMotivi);
          if (typeof d.consegnaStato === "string") setConsegnaStato(d.consegnaStato);
          if (typeof d.modalitaVisita === "string") setModalitaVisita(d.modalitaVisita);
          if (typeof d.modalitaSubentro === "string") setModalitaSubentro(d.modalitaSubentro);
          if (typeof d.luogoFirma === "string") setLuogoFirma(d.luogoFirma);
          if (typeof d.dataFirma === "string") setDataFirma(d.dataFirma);
          if (typeof d._savedAt === "number") setDraftSavedAt(d._savedAt);
        }
      }
    } catch { /* ignore corrupted draft */ }
    // Set DOPO i setter: React batcha tutto in un unico re-render e la save
    // effect vedrà sia `draftLoaded=true` sia lo stato aggiornato dalla bozza.
    setDraftLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftKey, draftLoaded]);

  // Reset gate quando il modal viene chiuso o cambia pratica → al prossimo
  // open la load effect riparte da capo.
  useEffect(() => {
    if (!open) setDraftLoaded(false);
  }, [open, draftKey]);

  // 2) Save on every state change — synchronous, no debounce. Attivo solo
  //    DOPO che la bozza eventuale è stata caricata.
  useEffect(() => {
    if (!draftKey || !draftLoaded) return;
    const snapshot = {
      contractType, locatori, conduttori, porzioneUsoRegolato, immobile, ape, tabelle,
      contratto, canone, deposito, altreGaranzie, garanti, cedolareSecca,
      esigenzaTransitorieta, esigenzaLocatore, esigenzaConduttore, documentiEsigenza,
      corso, ateneo, quotaOneriMensile, conviventi, recessoMesi, recessoGraviMotivi,
      consegnaStato, modalitaVisita, modalitaSubentro, luogoFirma, dataFirma,
      _savedAt: Date.now(),
    };
    try {
      localStorage.setItem(draftKey, JSON.stringify(snapshot));
      setDraftSavedAt(snapshot._savedAt);
    } catch { /* localStorage full / disabled */ }
  }, [
    draftKey, draftLoaded,
    contractType, locatori, conduttori, porzioneUsoRegolato, immobile, ape, tabelle,
    contratto, canone, deposito, altreGaranzie, cedolareSecca,
    esigenzaTransitorieta, esigenzaLocatore, esigenzaConduttore, documentiEsigenza,
    corso, ateneo, quotaOneriMensile, conviventi, recessoMesi, recessoGraviMotivi,
    consegnaStato, modalitaVisita, modalitaSubentro, luogoFirma, dataFirma,
  ]);

  // 3) Tick every 15s to refresh the "Xs fa" indicator
  useEffect(() => {
    if (!open || !draftSavedAt) return;
    const i = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(i);
  }, [open, draftSavedAt]);

  const draftAgoLabel = useMemo(() => {
    if (!draftSavedAt) return "";
    const diffSec = Math.max(0, Math.floor((nowTick - draftSavedAt) / 1000));
    if (diffSec < 5) return "ora";
    if (diffSec < 60) return `${diffSec}s fa`;
    const m = Math.floor(diffSec / 60);
    if (m < 60) return `${m} min fa`;
    const h = Math.floor(m / 60);
    return `${h} h fa`;
  }, [draftSavedAt, nowTick]);

  const discardDraft = () => {
    if (!draftKey) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm("Scartare la bozza salvata? Le modifiche fatte nel modal verranno perse al prossimo riavvio della pagina.");
    if (!ok) return;
    try { localStorage.removeItem(draftKey); } catch {}
    setDraftSavedAt(null);
    toast.success("Bozza scartata");
  };

  // Punto 20 (revisione) — mapping chip → setter di form state.
  // Solo i campi editabili nel form (whitelist backend `_EDITABLE_FIELDS`)
  // sono cliccabili nella preview: quando l'utente digita in un chip,
  // aggiorniamo il rispettivo state locale in modo bidirezionale.
  const handleFieldChange = (name, value) => {
    switch (name) {
      case "esigenza_locatore": setEsigenzaLocatore(value); break;
      case "esigenza_conduttore": setEsigenzaConduttore(value); break;
      case "esigenza_transitorieta": setEsigenzaTransitorieta(value); break;
      case "documenti_esigenza": setDocumentiEsigenza(value); break;
      case "iban": setCanone((c) => ({ ...c, iban: value.toUpperCase() })); break;
      case "n_rate": setCanone((c) => ({ ...c, numero_rate: value })); break;
      case "scadenze": setCanone((c) => ({ ...c, scadenze_rate: value })); break;
      case "piano": /* piano is locked — auto-filled from Step 4 (dati immobile); ignore any edit from the WYSIWYG chip */ break;
      case "scala": setImmobile((im) => ({ ...im, scala: value })); break;
      case "interno": setImmobile((im) => ({ ...im, interno: value })); break;
      case "vani": setImmobile((im) => ({ ...im, vani: value })); break;
      case "porzione_uso": setPorzioneUsoRegolato(value); break;
      case "prop": setTabelle((t) => ({ ...t, proprieta: value })); break;
      case "riscaldamento": setTabelle((t) => ({ ...t, riscaldamento: value })); break;
      case "acqua": setTabelle((t) => ({ ...t, acqua: value })); break;
      case "altre": setTabelle((t) => ({ ...t, altre: value })); break;
      case "ape_num": setApe((a) => ({ ...a, numero: value })); break;
      case "deposito_importo": setDeposito((d) => ({ ...d, importo: value })); break;
      case "deposito_mensilita": setDeposito((d) => ({ ...d, mensilita: value })); break;
      case "altre_garanzie": setAltreGaranzie(value); break;
      case "corso_studi": setCorso(value); break;
      case "ateneo": setAteneo(value); break;
      case "quota_oneri_mensile": setQuotaOneriMensile(value); break;
      case "conviventi": setConviventi(value); break;
      case "stato_immobile_consegna": setConsegnaStato(value); break;
      case "modalita_visita": setModalitaVisita(value); break;
      case "modalita_subentro": setModalitaSubentro(value); break;
      case "luogo_firma": setLuogoFirma(value); break;
      default: break; // any other placeholder is not user-editable
    }
  };

  // Reset when modal is opened
  useEffect(() => {
    if (open) {
      setStep(1);
      const t = data?.contratto?.tipologia === "transitorio"
        ? "transitorio"
        : data?.contratto?.tipologia === "studenti"
        ? "studenti"
        : "abitativo";
      setContractType(t);
      setLocatori(
        Array.isArray(data?.locatori) && data.locatori.length
          ? data.locatori.map((p) => ({ ...EMPTY_PARTY, nome_cognome: p?.nome || "" }))
          : [{ ...EMPTY_PARTY, nome_cognome: data?.locatore?.nome || "" }]
      );
      setConduttori(
        Array.isArray(data?.conduttori) && data.conduttori.length
          ? data.conduttori.map((p) => ({ ...EMPTY_PARTY, nome_cognome: p?.nome || "" }))
          : [{ ...EMPTY_PARTY, nome_cognome: data?.conduttore?.nome || "" }]
      );
      setImmobile({ piano: data?.immobile?.piano || "", scala: "", interno: "", vani: "" });
      // Durata + date sempre allineate a Step 6 (attestazione = contratto).
      const durataValore = data?.contratto?.durata_valore;
      const step6Anni = (() => {
        if (data?.contratto?.tipologia !== "abitativo" || !durataValore) return 3;
        const m = String(durataValore).match(/^(\d+)/);
        return m ? Number(m[1]) : 3;
      })();
      const step6Mesi = (() => {
        if (data?.contratto?.tipologia === "abitativo" || !durataValore) return 12;
        const n = Number(String(durataValore).replace(/\D/g, ""));
        return n > 0 ? n : 12;
      })();
      setContratto({
        durata_anni: step6Anni,
        durata_mesi: step6Mesi,
        data_inizio: data?.contratto?.data_inizio || "",
        data_fine: data?.contratto?.data_fine || "",
      });
      // Prefill canone dal canone mensile richiesto in Step 10.
      //
      // Regola (richiesta utente):
      //   - durata TOTALE del contratto ≥ 12 mesi → "Canone annuo"  = mensile × 12
      //     (anche quando il contratto dura 36 mesi, 24 mesi, ecc. — NON è il
      //      canone del periodo totale)
      //   - durata TOTALE del contratto <  12 mesi → "Canone del periodo"
      //       • contratti NON transitori     → mensile × mesi
      //       • contratti transitori         → mensile × mesi + (mensile ÷ 30) × giorni
      //         (per supportare le durate frazionarie "2 mesi e 15 giorni")
      const canoneMensile = Number(data?.step9?.canone_convenuto) || 0;
      const isAbitativoInit =
        !data?.contratto?.tipologia ||
        data?.contratto?.tipologia === "abitativo" ||
        data?.contratto?.tipologia === "ordinario";
      const totalMonthsInit = isAbitativoInit ? step6Anni * 12 : step6Mesi;
      const totalDaysInit = Number(data?.contratto?.durata_giorni) || 0;
      let canoneBase = "";
      if (canoneMensile > 0) {
        if (totalMonthsInit >= 12) {
          canoneBase = Math.round(canoneMensile * 12 * 100) / 100;
        } else if (totalMonthsInit > 0) {
          const base = canoneMensile * totalMonthsInit;
          const extra =
            data?.contratto?.tipologia === "transitorio" && totalDaysInit > 0
              ? (canoneMensile * totalDaysInit) / 30
              : 0;
          canoneBase = Math.round((base + extra) * 100) / 100;
        }
      }
      setCanone((c) => ({
        ...c,
        canone_annuo: canoneBase,
        // rata verrà ricalcolata automaticamente dall'effetto di sync in base
        // al numero di rate scelto (default 12).
        rata_mensile: "",
      }));
      setDeposito({ versato: true, importo: "", mensilita: 2 });
      setCedolareSecca(false);
      setEsigenzaTransitorieta("");
      setEsigenzaLocatore("");
      setEsigenzaConduttore("");
      setDocumentiEsigenza("");
      setCorso("");
      setAteneo("");
      setModalitaSubentro("");
    }
  }, [open, data]);

  const pertinenze = data?.catasto?.pertinenze || [];
  const catasto = {
    foglio: data?.catasto?.foglio,
    particella: data?.catasto?.particella,
    subalterno: data?.catasto?.subalterno,
    categoria: data?.catasto?.categoria,
    rendita: data?.catasto?.rendita,
  };

  const addLocatore = () => setLocatori([...locatori, { ...EMPTY_PARTY }]);
  const addConduttore = () => setConduttori([...conduttori, { ...EMPTY_PARTY }]);
  const updateLocatore = (i, p) => setLocatori(locatori.map((x, j) => (j === i ? p : x)));
  const updateConduttore = (i, p) => setConduttori(conduttori.map((x, j) => (j === i ? p : x)));
  const removeLocatore = (i) => setLocatori(locatori.filter((_, j) => j !== i));
  const removeConduttore = (i) => setConduttori(conduttori.filter((_, j) => j !== i));

  const submit = async () => {
    // Basic validation
    if (!locatori[0]?.nome_cognome?.trim()) {
      toast.error("Inserisci almeno il nome del locatore");
      return;
    }
    if (!conduttori[0]?.nome_cognome?.trim()) {
      toast.error("Inserisci almeno il nome del conduttore");
      return;
    }
    // Punto 2 — documento di riconoscimento obbligatorio per tutti i locatori/conduttori
    for (let i = 0; i < locatori.length; i++) {
      if (!locatori[i]?.documento?.trim()) {
        toast.error(`Inserisci il documento di riconoscimento del locatore n.${i + 1}`);
        return;
      }
    }
    for (let i = 0; i < conduttori.length; i++) {
      if (!conduttori[i]?.documento?.trim()) {
        toast.error(`Inserisci il documento di riconoscimento del conduttore n.${i + 1}`);
        return;
      }
    }
    // Blocco pratica se contratto TRANSITORIO + conduttore residente a Milano.
    // Nota: per il contratto Studenti universitari questa restrizione NON si
    // applica — gli studenti possono essere residenti a Milano.
    if (contractType === "transitorio") {
      const milanoResidente = conduttori.some((c) =>
        String(c?.residenza || "").trim().toLowerCase() === "milano"
      );
      if (milanoResidente) {
        toast.error(
          "Per redigere un contratto transitorio il conduttore NON può essere residente nel Comune di Milano.",
          { duration: 6000 }
        );
        return;
      }
    }
    // Punto 8 — APE obbligatoria
    if (!ape?.numero?.trim() || !ape?.data) {
      toast.error("Inserisci numero e data dell'Attestazione di Prestazione Energetica (APE)");
      return;
    }
    if (!canone.canone_annuo || Number(canone.canone_annuo) <= 0) {
      toast.error("Inserisci il canone annuo di locazione (torna allo Step 10 per calcolarlo)");
      return;
    }
    // Punto 11 — deposito ≤ 3 mensilità del canone
    if (deposito?.versato) {
      const canoneAnnuo = Number(canone.canone_annuo) || 0;
      const canoneMensile = canoneAnnuo / 12;
      const importoDep = Number(deposito.importo) || 0;
      if (canoneMensile > 0 && importoDep > canoneMensile * 3 + 0.01) {
        toast.error(
          `Il deposito cauzionale (€ ${importoDep.toFixed(2)}) supera il massimo di 3 mensilità del canone (€ ${(canoneMensile * 3).toFixed(2)}).`,
          { duration: 6000 }
        );
        return;
      }
    }
    if (contractType === "transitorio" && !esigenzaLocatore.trim() && !esigenzaConduttore.trim() && !esigenzaTransitorieta.trim()) {
      toast.error("Indica almeno una esigenza (del locatore o del conduttore) che giustifica la transitorietà");
      return;
    }
    if (contractType === "studenti" && (!corso.trim() || !ateneo.trim())) {
      toast.error("Indica il corso di studi e l'ateneo");
      return;
    }
    // Requirement 2 — Oneri accessori sono obbligatori
    if (!quotaOneriMensile || String(quotaOneriMensile).trim() === "" || Number(quotaOneriMensile) < 0) {
      toast.error("Inserisci l'importo degli oneri accessori (quota mensile) — è obbligatorio");
      return;
    }
    // Requirement 5 — la data della firma non può essere successiva alla data di inizio del contratto
    if (dataFirma && contratto?.data_inizio && dataFirma > contratto.data_inizio) {
      toast.error(
        `La data della firma (${dataFirma}) non può essere successiva alla data di inizio del contratto (${contratto.data_inizio}).`,
        { duration: 6000 }
      );
      return;
    }
    if (!submissionId) {
      toast.error("Crea prima una richiesta compilando lo Step 2");
      return;
    }

    const payload = {
      locatori,
      conduttori,
      garanti: garanti.map((nome) => ({ nome })),
      oggetto_scope: oggettoScope,
      porzione_uso_regolato: porzioneUsoRegolato,
      immobile: { ...(data?.immobile || {}), ...immobile },
      catasto,
      pertinenze,
      ammobiliata,
      ape_numero: ape.numero,
      ape_data: ape.data,
      tabelle,
      contratto: contractType === "abitativo"
        ? { durata_anni: contratto.durata_anni, data_inizio: contratto.data_inizio, data_fine: contratto.data_fine }
        : {
            durata_mesi: contratto.durata_mesi,
            durata_giorni: Number(data?.contratto?.durata_giorni) || 0,
            durata_label: data?.contratto?.durata_label || "",
            data_inizio: contratto.data_inizio,
            data_fine: contratto.data_fine,
          },
      canone_annuo: Number(canone.canone_annuo),
      numero_rate: Number(canone.numero_rate) || 12,
      rata_mensile: canone.rata_mensile ? Number(canone.rata_mensile) : undefined,
      scadenze_rate: canone.scadenze_rate,
      iban: canone.iban,
      deposito: {
        versato: deposito.versato,
        importo: deposito.importo ? Number(deposito.importo) : undefined,
        mensilita: deposito.mensilita,
      },
      altre_garanzie: altreGaranzie,
      cedolare_secca: cedolareSecca,
      esigenza_transitorieta: esigenzaTransitorieta,
      esigenza_locatore: esigenzaLocatore,
      esigenza_conduttore: esigenzaConduttore,
      documenti_esigenza: documentiEsigenza,
      corso_studi: corso,
      ateneo: ateneo,
      // Fase 2 — nuovi campi editabili
      quota_oneri_mensile: quotaOneriMensile,
      conviventi: conviventi,
      recesso_conduttore_mesi: `${recessoMesi} ${recessoMesi === 1 ? "mese" : "mesi"}`,
      recesso_gravi_motivi: !!recessoGraviMotivi,
      stato_immobile_consegna: consegnaStato,
      modalita_visita: modalitaVisita,
      luogo_firma: luogoFirma,
      data_firma: dataFirma,
    };

    setSubmitting(true);
    try {
      const r = await api.post("/generate-contract", {
        submission_id: submissionId,
        contract_type: contractType,
        payload,
      });
      toast.success("Contratto generato e caricato correttamente");
      if (onGenerated) onGenerated(r.data);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const autoRata = canone.canone_annuo && Number(canone.numero_rate) > 0
    ? (Number(canone.canone_annuo) / Number(canone.numero_rate)).toFixed(2)
    : "";

  // Keep `canone.rata_mensile` in sync with the computed value — the rata is
  // ALWAYS `canone / n_rate` (mensile, bimestrale, trimestrale, annuale…
  // dipende da quante rate l'utente indica). Il campo è read-only per l'utente,
  // quindi possiamo sovrascriverlo senza rispettare edits.
  useEffect(() => {
    const target = autoRata || "";
    if (String(target) !== String(canone.rata_mensile || "")) {
      setCanone((c) => ({ ...c, rata_mensile: target }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRata]);

  // Deposito cauzionale — "mensilità" si auto-compila a partire da
  //   mensilità = importo / canone mensile (NON rata).
  //   Il campo mensilità non è più editabile manualmente.
  useEffect(() => {
    if (!deposito.versato) return;
    const canoneMensileBase = Number(data?.step9?.canone_convenuto) || 0;
    const importoNum = Number(deposito.importo) || 0;
    let mensilita = "";
    if (canoneMensileBase > 0 && importoNum > 0) {
      // Round a 1 decimale (es. 1500/1000 = 1.5)
      mensilita = Math.round((importoNum / canoneMensileBase) * 10) / 10;
    } else if (importoNum === 0) {
      mensilita = 0;
    }
    if (String(deposito.mensilita ?? "") !== String(mensilita)) {
      setDeposito((d) => ({ ...d, mensilita }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposito.importo, deposito.versato, data?.step9?.canone_convenuto]);

  // Etichette dinamiche in base al tipo di contratto e alla durata totale.
  //   • durata ≥ 12 mesi → "Canone annuo" (anche per 36 mesi: NON è il totale)
  //   • durata <  12 mesi → "Canone del periodo"
  //       - per i transitori con giorni frazionari aggiungiamo "e N giorni"
  //         nell'etichetta per chiarire che il valore comprende i giorni.
  const isAbitativo = contractType === "abitativo";
  const totalMonthsForLabel = isAbitativo
    ? (Number(contratto.durata_anni) || 0) * 12
    : (Number(contratto.durata_mesi) || 0);
  const giorniLabel = contractType === "transitorio"
    ? (Number(data?.contratto?.durata_giorni) || 0)
    : 0;
  const canoneLabel = (() => {
    if (totalMonthsForLabel >= 12) return "Canone annuo (€) *";
    if (totalMonthsForLabel > 0) {
      const mesiTxt = `${totalMonthsForLabel} ${totalMonthsForLabel === 1 ? "mese" : "mesi"}`;
      const giorniTxt = giorniLabel > 0 ? ` e ${giorniLabel} ${giorniLabel === 1 ? "giorno" : "giorni"}` : "";
      return `Canone del periodo (${mesiTxt}${giorniTxt}) (€) *`;
    }
    return "Canone del periodo (€) *";
  })();

  // Punto 20 — payload usato SIA per l'anteprima live SIA come base per il PDF ReportLab.
  // Contract-preview e generate-contract accettano lo stesso schema, quindi manteniamo un
  // unico oggetto (evitiamo drift). Ricalcolato ad ogni render — piccolo footprint.
  const previewPayload = useMemo(() => ({
    contract_type: contractType,
    payload: {
      oggetto_scope: oggettoScope,
      porzione_uso_regolato: porzioneUsoRegolato,
      locatori,
      conduttori,
      garanti: garanti.map((nome) => ({ nome })),
      immobile: { ...(data?.immobile || {}), ...immobile },
      catasto,
      pertinenze,
      ammobiliata,
      ape_numero: ape.numero,
      ape_data: ape.data,
      tabelle,
      contratto: contractType === "abitativo"
        ? { durata_anni: contratto.durata_anni, data_inizio: contratto.data_inizio, data_fine: contratto.data_fine }
        : {
            durata_mesi: contratto.durata_mesi,
            durata_giorni: Number(data?.contratto?.durata_giorni) || 0,
            durata_label: data?.contratto?.durata_label || "",
            data_inizio: contratto.data_inizio,
            data_fine: contratto.data_fine,
          },
      canone_annuo: canone.canone_annuo ? Number(canone.canone_annuo) : undefined,
      numero_rate: Number(canone.numero_rate) || 12,
      rata_mensile: canone.rata_mensile ? Number(canone.rata_mensile) : undefined,
      scadenze_rate: canone.scadenze_rate,
      iban: canone.iban,
      deposito: {
        versato: deposito.versato,
        importo: deposito.importo ? Number(deposito.importo) : undefined,
        mensilita: deposito.mensilita,
      },
      altre_garanzie: altreGaranzie,
      cedolare_secca: cedolareSecca,
      esigenza_transitorieta: esigenzaTransitorieta,
      esigenza_locatore: esigenzaLocatore,
      esigenza_conduttore: esigenzaConduttore,
      documenti_esigenza: documentiEsigenza,
      corso_studi: corso,
      ateneo,
      quota_oneri_mensile: quotaOneriMensile,
      conviventi,
      recesso_conduttore_mesi: `${recessoMesi} ${recessoMesi === 1 ? "mese" : "mesi"}`,
      recesso_gravi_motivi: !!recessoGraviMotivi,
      stato_immobile_consegna: consegnaStato,
      modalita_visita: modalitaVisita,
      modalita_subentro: modalitaSubentro,
      luogo_firma: luogoFirma,
      data_firma: dataFirma,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contractType, oggettoScope, porzioneUsoRegolato, JSON.stringify(locatori), JSON.stringify(conduttori),
       JSON.stringify(immobile), JSON.stringify(catasto), JSON.stringify(pertinenze), ammobiliata,
       JSON.stringify(ape), JSON.stringify(tabelle), JSON.stringify(contratto), JSON.stringify(canone),
       JSON.stringify(deposito), altreGaranzie, JSON.stringify(garanti), cedolareSecca,
       esigenzaTransitorieta, esigenzaLocatore, esigenzaConduttore, documentiEsigenza,
       corso, ateneo, quotaOneriMensile, conviventi, recessoMesi, recessoGraviMotivi,
       consegnaStato, modalitaVisita, modalitaSubentro, luogoFirma, dataFirma, data?.immobile]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden p-0" data-testid="contract-gen-modal">
        <div className="p-6 pb-3 border-b border-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-slate-700" />
              Generatore Contratto di Locazione
            </DialogTitle>
            {step !== 2 && (
              <DialogDescription>
                Compila la maschera a sinistra — sulla destra vedi in tempo reale il <strong>testo integrale del contratto</strong> con tutte le clausole. Il PDF finale conterrà lo stesso testo e verrà caricato automaticamente nello slot &quot;Contratto di locazione&quot;.
              </DialogDescription>
            )}
          </DialogHeader>
          {step !== 2 && draftKey && (
            <div className="mt-2 flex items-center gap-3 text-[11px]" data-testid="cg-draft-indicator">
              {draftSavedAt ? (
                <>
                  <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="cg-draft-saved">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Bozza salvata · {draftAgoLabel}
                  </span>
                  <button
                    type="button"
                    onClick={discardDraft}
                    className="text-slate-500 hover:text-red-700 underline underline-offset-2"
                    data-testid="cg-draft-discard"
                  >
                    Scarta bozza
                  </button>
                </>
              ) : (
                <span className="text-slate-500" data-testid="cg-draft-waiting">
                  Le modifiche verranno salvate automaticamente in questo browser
                </span>
              )}
            </div>
          )}
        </div>

        <div style={{maxHeight:"calc(92vh - 130px)"}} className="overflow-hidden">
        {step === 2 ? (
          <div className="p-6 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-3xl mb-3">✓</div>
            <p className="font-medium text-slate-900 text-lg">Contratto generato</p>
            <p className="text-sm text-slate-500 mt-1">
              Il PDF è stato caricato nello slot &quot;Contratto di locazione&quot;.
              Puoi comunque sostituirlo con un file diverso in qualsiasi momento.
            </p>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="mt-5 px-5 py-2 rounded-md bg-slate-900 text-white text-sm font-medium"
              data-testid="cg-close-success"
            >
              Torna allo Step 11
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-0 h-full max-h-full overflow-hidden">
          <div className="overflow-y-auto p-6 space-y-5" data-testid="cg-form-column">
            {/* Contract type — locked to Step 6 selection */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="cg-contract-type-locked">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Tipologia contrattuale</p>
              <p className="text-sm font-medium text-slate-900">
                {contractType === "abitativo" && "Abitativo — canone concordato (3+2 anni)"}
                {contractType === "transitorio" && "Transitorio (1–18 mesi)"}
                {contractType === "studenti" && "Studenti universitari (6–36 mesi)"}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Determinato dalla scelta effettuata nello Step 6. Per cambiarlo torna allo Step 6.
              </p>
            </div>

            {/* Locatori */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-slate-800">Locatori</p>
                <button type="button" onClick={addLocatore} className="text-xs flex items-center gap-1 text-slate-700 hover:text-slate-900" data-testid="cg-add-locatore">
                  <Plus size={13} /> Aggiungi locatore
                </button>
              </div>
              <div className="space-y-2">
                {locatori.map((p, i) => (
                  <PartyEditor key={i} role="locatore" index={i} party={p} onChange={(np) => updateLocatore(i, np)} onRemove={() => removeLocatore(i)} canRemove={locatori.length > 1} />
                ))}
              </div>
            </div>

            {/* Conduttori */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-slate-800">Conduttori</p>
                <button type="button" onClick={addConduttore} className="text-xs flex items-center gap-1 text-slate-700 hover:text-slate-900" data-testid="cg-add-conduttore">
                  <Plus size={13} /> Aggiungi conduttore
                </button>
              </div>
              <div className="space-y-2">
                {conduttori.map((p, i) => (
                  <PartyEditor key={i} role="conduttore" index={i} party={p} onChange={(np) => updateConduttore(i, np)} onRemove={() => removeConduttore(i)} canRemove={conduttori.length > 1} />
                ))}
              </div>
            </div>

            {/* Immobile scope */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-slate-800 mb-2">Oggetto della locazione</p>
              <div className="flex gap-2 mb-1">
                {[
                  { v: "totale", l: "Intera unità immobiliare" },
                  { v: "porzione", l: "Solo una porzione (stanza / locali)" },
                ].map((o) => {
                  const active = oggettoScope === o.v;
                  return (
                    <button
                      key={o.v}
                      type="button"
                      disabled
                      aria-disabled="true"
                      title="Impostato dallo Step 8. Per modificare torna allo Step 8."
                      className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors cursor-not-allowed ${
                        active
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-slate-50 text-slate-400 border-slate-200"
                      }`}
                      data-testid={`cg-scope-${o.v}`}
                    >
                      {o.l}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 italic mb-3">
                Impostato dallo Step 8 "Tipologia Superficie e MQ"{" "}
                {oggettoScope === "porzione"
                  ? "(Affitto solo una porzione dell'appartamento = SÌ)"
                  : "(intera unità locata)"}
                . Per modificare torna allo Step 8.
              </p>
              {oggettoScope === "porzione" && (
                <div className="mb-3">
                  <Label className="text-xs">Descrizione porzione e uso condiviso</Label>
                  <Textarea rows={2} className="mt-1" value={porzioneUsoRegolato} onChange={(e) => setPorzioneUsoRegolato(e.target.value)} data-testid="cg-porzione-uso" />
                </div>
              )}

              <div className="grid sm:grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs">Piano</Label>
                  <Input
                    className="mt-1 h-9 bg-slate-50 text-slate-700 cursor-not-allowed"
                    value={immobile.piano}
                    readOnly
                    disabled
                    data-testid="cg-immobile-piano"
                  />
                  <p className="text-[10px] text-slate-500 mt-1 italic">Bloccato: auto-compilato dai dati dell&apos;immobile.</p>
                </div>
                <div>
                  <Label className="text-xs">Scala</Label>
                  <Input className="mt-1 h-9" value={immobile.scala} onChange={(e) => setImmobile({ ...immobile, scala: e.target.value })} data-testid="cg-immobile-scala" />
                </div>
                <div>
                  <Label className="text-xs">Interno</Label>
                  <Input className="mt-1 h-9" value={immobile.interno} onChange={(e) => setImmobile({ ...immobile, interno: e.target.value })} data-testid="cg-immobile-interno" />
                </div>
                <div>
                  <Label className="text-xs">Vani</Label>
                  <Input className="mt-1 h-9" value={immobile.vani} onChange={(e) => setImmobile({ ...immobile, vani: e.target.value })} data-testid="cg-immobile-vani" />
                </div>
              </div>

              <div className="mt-3" data-testid="cg-ammobiliata-wrap">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-not-allowed select-none">
                  <Checkbox
                    checked={ammobiliata}
                    disabled
                    data-testid="cg-ammobiliata"
                  />
                  <span className="text-slate-500">
                    Unità ammobiliata (elenco a parte){" "}
                    <span className="font-medium text-slate-900">
                      {ammobiliata ? "SÌ" : "NO"}
                    </span>
                  </span>
                </label>
                <p className="text-[11px] text-slate-500 mt-1 ml-6">
                  Impostato dallo Step 9 "Maggiorazioni/Riduzioni" → Alloggio ammobiliato. Per modificarlo torna allo Step 9.
                </p>
              </div>

              {pertinenze.length > 0 && (
                <p className="text-xs text-slate-500 mt-2">
                  Elementi accessori inclusi dallo Step 5: <strong>{pertinenze.map((p) => p.tipo).join(", ")}</strong>
                </p>
              )}
            </div>

            {/* APE */}
            <div className="grid sm:grid-cols-2 gap-2 border-t pt-4">
              <div>
                <Label className="text-xs">APE — numero</Label>
                <Input className="mt-1 h-9" value={ape.numero} onChange={(e) => setApe({ ...ape, numero: e.target.value })} data-testid="cg-ape-num" />
              </div>
              <div>
                <Label className="text-xs">APE — data</Label>
                <Input type="date" className="mt-1 h-9" value={ape.data} onChange={(e) => setApe({ ...ape, data: e.target.value })} data-testid="cg-ape-data" />
              </div>
            </div>

            {/* Durata e date */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-slate-800 mb-2">Durata contrattuale</p>
              <div className="grid sm:grid-cols-3 gap-2">
                {contractType === "abitativo" ? (
                  <div>
                    <Label className="text-xs">Durata (anni, min. 3)</Label>
                    <Input type="number" min="3" className="mt-1 h-9 bg-slate-100 cursor-not-allowed" value={contratto.durata_anni} readOnly data-testid="cg-durata-anni" />
                  </div>
                ) : contractType === "transitorio" ? (
                  <div>
                    <Label className="text-xs">Durata</Label>
                    <Input
                      type="text"
                      className="mt-1 h-9 bg-slate-100 cursor-not-allowed"
                      value={
                        data?.contratto?.durata_label ||
                        (() => {
                          const m = Number(data?.contratto?.durata_valore) || 0;
                          const g = Number(data?.contratto?.durata_giorni) || 0;
                          const parts = [];
                          if (m) parts.push(`${m} ${m === 1 ? "mese" : "mesi"}`);
                          if (g) parts.push(`${g} ${g === 1 ? "giorno" : "giorni"}`);
                          return parts.length ? parts.join(" e ") : "";
                        })()
                      }
                      readOnly
                      data-testid="cg-durata-transitorio"
                    />
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">Durata (mesi)</Label>
                    <Input type="number" min="1" className="mt-1 h-9 bg-slate-100 cursor-not-allowed" value={contratto.durata_mesi} readOnly data-testid="cg-durata-mesi" />
                  </div>
                )}
                <div>
                  <Label className="text-xs">Data inizio</Label>
                  <Input type="date" className="mt-1 h-9 bg-slate-100 cursor-not-allowed" value={contratto.data_inizio} readOnly data-testid="cg-data-inizio" />
                </div>
                <div>
                  <Label className="text-xs">Data fine</Label>
                  <Input type="date" className="mt-1 h-9 bg-slate-100 cursor-not-allowed" value={contratto.data_fine} readOnly data-testid="cg-data-fine" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-2" data-testid="cg-durata-linked">
                Durata e date sono collegate allo Step 6 per garantire coerenza tra attestazione bilaterale e contratto. Per modificarle torna allo Step 6.
              </p>
            </div>

            {/* Canone + garanzie */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-slate-800 mb-2">Canone e modalità di pagamento</p>
              {Number(data?.step9?.canone_convenuto) > 0 ? (
                <p className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 mb-2" data-testid="cg-canone-linked-info">
                  Il canone <strong>mensile</strong> è collegato al valore inserito nello Step 10 (€ {Number(data.step9.canone_convenuto).toFixed(2)} · escluse spese) e non può essere modificato qui per garantire coerenza tra contratto e attestazione bilaterale.
                </p>
              ) : (
                <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mb-2" data-testid="cg-canone-missing-warn">
                  <strong>Attenzione:</strong> il canone mensile non risulta calcolato nello Step 10. Torna allo Step 10 per definire il canone convenuto prima di generare il contratto.
                </p>
              )}
              <div className="grid sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">{canoneLabel}</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    className="mt-1 h-9 bg-slate-100 cursor-not-allowed"
                    value={canone.canone_annuo}
                    readOnly
                    data-testid="cg-canone-annuo"
                  />
                </div>
                <div>
                  <Label className="text-xs">N. rate</Label>
                  <Input type="number" min="1" max="12" className="mt-1 h-9" value={canone.numero_rate} onChange={(e) => setCanone({ ...canone, numero_rate: e.target.value })} data-testid="cg-numero-rate" />
                </div>
                <div>
                  <Label className="text-xs">Rata (€)</Label>
                  <Input
                    type="number" min="0" step="0.01" placeholder={autoRata}
                    className="mt-1 h-9 bg-slate-100 cursor-not-allowed"
                    value={canone.rata_mensile}
                    readOnly
                    data-testid="cg-rata-mensile"
                  />
                  <p className="text-[10px] text-slate-500 mt-1 italic">
                    Calcolata automaticamente: canone ÷ n. rate.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">IBAN per bonifico</Label>
                  <Input className="mt-1 h-9" placeholder="IT60 X054 2811 1010 0000 0123 456" value={canone.iban} onChange={(e) => setCanone({ ...canone, iban: e.target.value.toUpperCase() })} data-testid="cg-iban" />
                </div>
                <div>
                  <Label className="text-xs">Scadenza rate</Label>
                  <Input className="mt-1 h-9" value={canone.scadenze_rate} onChange={(e) => setCanone({ ...canone, scadenze_rate: e.target.value })} data-testid="cg-scadenze" />
                </div>
              </div>
            </div>

            {/* Deposito */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-slate-800 mb-2">Deposito cauzionale e garanzie</p>
              <label className="flex items-center gap-2 text-sm mb-2">
                <Checkbox checked={deposito.versato} onCheckedChange={(v) => setDeposito({ ...deposito, versato: !!v })} data-testid="cg-deposito-versato" />
                Il conduttore versa il deposito cauzionale
              </label>
              {deposito.versato && (() => {
                const canoneMensileBase = Number(data?.step9?.canone_convenuto) || 0;
                const depositoMax = canoneMensileBase > 0
                  ? Math.round(canoneMensileBase * 3 * 100) / 100
                  : null;
                const importoNum = Number(deposito.importo) || 0;
                const overCap = depositoMax !== null && importoNum > depositoMax;
                return (
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">
                        Importo (€){depositoMax !== null && (
                          <span className="text-slate-500 font-normal"> · max € {depositoMax.toFixed(2)} (3 mensilità)</span>
                        )}
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        max={depositoMax ?? undefined}
                        className={`mt-1 h-9 ${overCap ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`}
                        value={deposito.importo}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (depositoMax === null || raw === "" || Number(raw) <= depositoMax) {
                            setDeposito({ ...deposito, importo: raw });
                          } else {
                            // Clamp al massimo consentito (3 mensilità)
                            setDeposito({ ...deposito, importo: String(depositoMax) });
                          }
                        }}
                        data-testid="cg-deposito-importo"
                      />
                      {overCap && (
                        <p className="text-[11px] text-red-700 mt-1" data-testid="cg-deposito-importo-error">
                          Il deposito cauzionale non può eccedere le 3 mensilità di canone (€ {depositoMax.toFixed(2)}).
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Mensilità (max 3) <span className="text-slate-400 font-normal">· auto</span></Label>
                      <Input
                        type="number"
                        min="0"
                        max="3"
                        step="0.1"
                        className="mt-1 h-9 bg-slate-50 text-slate-700 cursor-not-allowed"
                        value={deposito.mensilita}
                        readOnly
                        disabled
                        data-testid="cg-deposito-mensilita"
                      />
                      <p className="text-[10px] text-slate-500 mt-1 italic">
                        Calcolato automaticamente: importo ÷ canone mensile.
                      </p>
                    </div>
                  </div>
                );
              })()}
              <div className="mt-2">
                <Label className="text-xs">Altre forme di garanzia (facoltativo)</Label>
                <Input className="mt-1 h-9" placeholder="Es. fideiussione bancaria, garanzia di terzi…" value={altreGaranzie} onChange={(e) => setAltreGaranzie(e.target.value)} data-testid="cg-altre-garanzie" />
                <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mt-2 leading-relaxed" data-testid="cg-fideiussione-info">
                  <strong>Attenzione:</strong> se la fideiussione indicata risulta posta a garanzia anche degli eventuali danni all&apos;immobile, la stessa deve intendersi <em>alternativa</em> al deposito cauzionale. La proprietà dovrà quindi scegliere se richiedere il deposito cauzionale <em>oppure</em> la garanzia fideiussoria, evitando la contemporanea previsione di entrambe per le medesime finalità.
                </p>
              </div>

              {/* Garanti (facoltativi) — ciascun garante firma dal portale pubblico conduttore */}
              <div className="mt-4 border-t pt-3" data-testid="cg-garanti-block">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <Label className="text-xs">Garanti (facoltativo)</Label>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Nome e cognome del/dei garante/i. Per ciascun garante inserito verrà aggiunto un blocco firma dedicato nel contratto e un campo firma nel portale conduttore.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGaranti([...(garanti || []), ""])}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                    data-testid="cg-garanti-add"
                  >
                    + Aggiungi garante
                  </button>
                </div>
                {garanti.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {garanti.map((nome, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`cg-garante-row-${i}`}>
                        <Input
                          className="h-9 flex-1"
                          placeholder={`Garante ${i + 1} — Nome e Cognome`}
                          value={nome}
                          onChange={(e) => {
                            const next = [...garanti];
                            next[i] = e.target.value;
                            setGaranti(next);
                          }}
                          data-testid={`cg-garante-nome-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => setGaranti(garanti.filter((_, j) => j !== i))}
                          className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                          data-testid={`cg-garante-remove-${i}`}
                          title="Rimuovi garante"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Cedolare secca — Yes/No radio */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-slate-800 mb-2">Regime fiscale — Cedolare secca</p>
              <p className="text-[11px] text-slate-600 mb-2">
                Ai sensi dell&apos;art. 3 del d.lgs. n. 23 del 14/03/2011. Con opzione affermativa non sono dovute imposte di bollo e registro; nel PDF sarà incluso l&apos;articolo &quot;Opzione cedolare secca&quot;. Con opzione negativa sarà incluso l&apos;articolo &quot;Spese di bollo e registrazione&quot;.
              </p>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="cg-cedolare-choice">
                {[
                  { v: true, label: "Sì, aderisco", helper: "Cedolare secca attiva" },
                  { v: false, label: "No, non aderisco", helper: "Regime IRPEF ordinario + registrazione" },
                ].map((o) => {
                  const active = cedolareSecca === o.v;
                  return (
                    <button
                      key={String(o.v)}
                      type="button"
                      onClick={() => setCedolareSecca(o.v)}
                      className={`text-left border rounded-md p-3 transition-colors ${
                        active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
                      }`}
                      data-testid={`cg-cedolare-${o.v ? "yes" : "no"}`}
                    >
                      <div className="text-sm font-medium">{o.label}</div>
                      <div className={`text-xs mt-0.5 ${active ? "text-slate-200" : "text-slate-500"}`}>{o.helper}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Specific fields */}
            {contractType === "transitorio" && (
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-semibold text-slate-800">Esigenze che giustificano la transitorietà</p>
                <p className="text-[11px] text-slate-500 -mt-1">
                  Compila almeno una delle due esigenze. Nell&apos;articolo 2 del contratto verranno riportate nell&apos;ordine ufficiale previsto dall&apos;Allegato B: prima l&apos;esigenza del <strong>locatore</strong> (lettera A), poi quella del <strong>conduttore</strong> (lettera B).
                </p>
                <div>
                  <Label className="text-xs">A) Esigenza del locatore</Label>
                  <Textarea rows={2} className="mt-1" placeholder="Es. destinazione dell'immobile a residenza propria o di un familiare entro X mesi, vendita programmata, lavori straordinari…" value={esigenzaLocatore} onChange={(e) => setEsigenzaLocatore(e.target.value)} data-testid="cg-esigenza-locatore" />
                </div>
                <div>
                  <Label className="text-xs">B) Esigenza del conduttore</Label>
                  <Textarea rows={2} className="mt-1" placeholder="Es. trasferimento lavorativo temporaneo di X mesi, ricongiungimento familiare, tirocinio…" value={esigenzaConduttore} onChange={(e) => setEsigenzaConduttore(e.target.value)} data-testid="cg-esigenza-conduttore" />
                </div>
                <div>
                  <Label className="text-xs">Documenti allegati (obbligatorio per durate &gt; 30 giorni)</Label>
                  <Input className="mt-1 h-9" placeholder="Es. contratto di lavoro, iscrizione ateneo, atto di vendita…" value={documentiEsigenza} onChange={(e) => setDocumentiEsigenza(e.target.value)} data-testid="cg-documenti-esigenza" />
                </div>
              </div>
            )}

            {contractType === "studenti" && (
              <div className="border-t pt-4 grid sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Corso di studi *</Label>
                  <Input className="mt-1 h-9" value={corso} onChange={(e) => setCorso(e.target.value)} data-testid="cg-corso" />
                </div>
                <div>
                  <Label className="text-xs">Università / Ateneo *</Label>
                  <Input className="mt-1 h-9" value={ateneo} onChange={(e) => setAteneo(e.target.value)} data-testid="cg-ateneo" />
                </div>
              </div>
            )}

            {/* Requirement 2 — Oneri accessori (obbligatori) */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Oneri accessori</h3>
              <div>
                <Label className="text-xs">
                  Quota mensile (€) <span className="text-red-700">*</span>
                </Label>
                <Input
                  type="number" step="0.01" min="0"
                  className={`mt-1 h-9 max-w-xs ${!quotaOneriMensile ? "border-amber-400" : ""}`}
                  placeholder="es. 50,00"
                  value={quotaOneriMensile}
                  onChange={(e) => setQuotaOneriMensile(e.target.value)}
                  data-testid="cg-oneri-mensili"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Campo obbligatorio: indica l&apos;importo mensile degli oneri accessori (condominio, riscaldamento…).
                </p>
              </div>
            </div>

            {/* Fase 2 — Clausole editabili opzionali */}
            <div className="border-t pt-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Clausole editabili (facoltative)</h3>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Recesso conduttore — preavviso (mesi)</Label>
                  <select
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 px-2 text-sm"
                    value={recessoMesi}
                    onChange={(e) => setRecessoMesi(Number(e.target.value))}
                    data-testid="cg-recesso-mesi"
                  >
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <option key={n} value={n}>{n} {n === 1 ? "mese" : "mesi"}</option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={recessoGraviMotivi}
                  onChange={(e) => setRecessoGraviMotivi(e.target.checked)}
                  data-testid="cg-recesso-gravi-motivi"
                />
                Includere la clausola &quot;per gravi motivi&quot; nell&apos;articolo Recesso
              </label>

              <div>
                <Label className="text-xs">Tabelle millesimali (facoltative)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                  <Input placeholder="Proprietà" value={tabelle.proprieta} onChange={(e) => setTabelle({ ...tabelle, proprieta: e.target.value })} data-testid="cg-tab-prop" className="h-9 text-sm" />
                  <Input placeholder="Riscaldamento" value={tabelle.riscaldamento} onChange={(e) => setTabelle({ ...tabelle, riscaldamento: e.target.value })} data-testid="cg-tab-risc" className="h-9 text-sm" />
                  <Input placeholder="Acqua" value={tabelle.acqua} onChange={(e) => setTabelle({ ...tabelle, acqua: e.target.value })} data-testid="cg-tab-acqua" className="h-9 text-sm" />
                  <Input placeholder="Altre" value={tabelle.altre} onChange={(e) => setTabelle({ ...tabelle, altre: e.target.value })} data-testid="cg-tab-altre" className="h-9 text-sm" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Persone conviventi con il conduttore</Label>
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm min-h-[60px]"
                  placeholder="es. la moglie Maria Rossi, i figli Giulio (2015) e Lucia (2018)"
                  value={conviventi}
                  onChange={(e) => setConviventi(e.target.value)}
                  data-testid="cg-conviventi"
                />
              </div>

              <div>
                <Label className="text-xs">Stato dell&apos;immobile alla consegna (art. 1590 C.C.)</Label>
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm min-h-[60px]"
                  placeholder="es. buono stato di manutenzione, pareti tinteggiate, pavimenti integri…"
                  value={consegnaStato}
                  onChange={(e) => setConsegnaStato(e.target.value)}
                  data-testid="cg-consegna-stato"
                />
              </div>

              <div>
                <Label className="text-xs">Modalità di visita (art. Accesso)</Label>
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm min-h-[50px]"
                  placeholder="es. martedì e giovedì dalle 16 alle 18 previo appuntamento"
                  value={modalitaVisita}
                  onChange={(e) => setModalitaVisita(e.target.value)}
                  data-testid="cg-modalita-visita"
                />
              </div>

              {/* Modalità di subentro — regole:
                  - transitorio: MAI (campo nascosto)
                  - studenti: SEMPRE
                  - abitativo: SOLO se più di un conduttore */}
              {contractType !== "transitorio" &&
                (contractType === "studenti" || conduttori.length > 1) && (
                <div>
                  <Label className="text-xs">Modalità di subentro (art. specifico)</Label>
                  <textarea
                    className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm min-h-[50px]"
                    placeholder="es. il subentro è consentito solo previa comunicazione scritta al locatore e assenso di quest'ultimo entro 30 giorni"
                    value={modalitaSubentro}
                    onChange={(e) => setModalitaSubentro(e.target.value)}
                    data-testid="cg-modalita-subentro"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {contractType === "abitativo"
                      ? "Comparirà nell'articolo \"Recesso del conduttore\". Se lasciata vuota, lo spazio resta in bianco."
                      : "Se compilata, comparirà nella clausola apposita del contratto. Se lasciata vuota, lo spazio resta in bianco."}
                  </p>
                </div>
              )}
            </div>

            {/* Firma */}
            <div className="border-t pt-4 grid sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Luogo firma</Label>
                <Input className="mt-1 h-9" value={luogoFirma} onChange={(e) => setLuogoFirma(e.target.value)} data-testid="cg-luogo-firma" />
              </div>
              <div>
                <Label className="text-xs">Data firma</Label>
                <Input
                  type="date"
                  className="mt-1 h-9"
                  value={dataFirma}
                  max={contratto?.data_inizio || undefined}
                  onChange={(e) => setDataFirma(e.target.value)}
                  data-testid="cg-data-firma"
                />
                {contratto?.data_inizio && (
                  <p className="text-[10px] text-slate-500 mt-1 italic">
                    Deve essere ≤ data di inizio contratto ({contratto.data_inizio}).
                  </p>
                )}
              </div>
            </div>

            <DialogFooter className="pt-3 border-t">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700 bg-white hover:bg-slate-50"
                data-testid="cg-cancel"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-black disabled:opacity-60 inline-flex items-center gap-2"
                data-testid="cg-submit"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {submitting ? "Generazione in corso…" : "Genera e allega PDF"}
              </button>
            </DialogFooter>
          </div>

          {/* Preview column — full contract text, chip-editable only in user fields */}
          <div className="hidden lg:block overflow-y-auto bg-slate-100 border-l border-slate-200 p-6" data-testid="cg-preview-column">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Anteprima contratto — modificabile nei campi</p>
              <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-medium" data-testid="cg-live-badge">Live · testo ufficiale</span>
            </div>
            <ContractEditablePreview
              payload={previewPayload}
              onFieldChange={handleFieldChange}
            />
            <p className="text-[10px] text-slate-400 italic mt-3">
              Puoi modificare direttamente i campi <span className="bg-yellow-100 border border-yellow-300 rounded px-1">evidenziati in giallo</span>: gli aggiornamenti si riflettono nel form a sinistra e nel PDF finale. Il testo legale delle clausole ufficiali resta bloccato.
            </p>
          </div>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
